import { useEffect } from 'react'
import { setMetaTags } from '@/utils/seo'
import { Link } from 'react-router-dom'
import { CheckCircle, ChevronRight, Sun } from 'lucide-react'
import SiluxPromise from '@/components/SiluxPromise'

const SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      "name": "Holiday Home Broadband UK — Mobile Internet for Second Homes",
      "provider": { "@type": "Organization", "name": "Silux Mobile", "url": "https://www.siluxmobile.co.uk" },
      "serviceType": "Mobile Broadband",
      "areaServed": "United Kingdom",
      "description": "Mobile broadband for holiday homes, cottages and second properties. No landline and no engineer required, router included and delivered, and it can move with you to another property. Choose a plan from 1 month (rolling) to 36 months, from £15.95 a month. No price rises during your contract and free broadband upgrades.",
      "url": "https://www.siluxmobile.co.uk/broadband-holiday-home"
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        { "@type": "Question", "name": "What is the best broadband for a holiday home?", "acceptedAnswer": { "@type": "Answer", "text": "Mobile broadband is the best option for holiday homes: no landline, no engineer and nothing to install in a property you are rarely at. You choose the term that matches how long you will keep the place, from 1 month: rolling monthly from £25.95 a month (30 days’ notice to leave), 3 months from £19.95 a month plus a £15 refundable holding deposit, or £15.95 a month on a 24-month plan. The router is included and can move to another property if you sell up." } },
        { "@type": "Question", "name": "Can I get broadband in a rural holiday cottage?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Many rural holiday cottages already have Three's 4G signal. Silux Mobile's data SIMs connect to Three's 5G/4G network and can provide 4G speeds of 10–50 Mbps on Broadband Lite and 50–150 Mbps on 5G Premium in most rural areas, and 80–400 Mbps where there is 5G — enough for streaming and working remotely." } },
        { "@type": "Question", "name": "Can holiday home guests use the broadband?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. Insert a Silux Mobile SIM into a WiFi router and guests connect as they would at home. No logging in each time — just share the WiFi password. The router stays in the property permanently." } },
        { "@type": "Question", "name": "Do I need a landline for holiday home broadband?", "acceptedAnswer": { "@type": "Answer", "text": "No. Silux Mobile's mobile broadband requires no landline and no engineer visit. This is especially valuable for rural holiday properties where getting a BT line installed is expensive and slow." } },
        { "@type": "Question", "name": "How much data does a holiday home need?", "acceptedAnswer": { "@type": "Answer", "text": "Every Silux Mobile broadband plan has unlimited data, so guests can browse, stream and video call with no speed throttling (fair use policy applies). Broadband Lite is £15.95 a month on a 24-month plan; for a busier property, 5G Premium starts at £19.95 a month." } },
        { "@type": "Question", "name": "Will my broadband price increase during my contract?", "acceptedAnswer": { "@type": "Answer", "text": "No. Your price is fixed for the life of your plan — there are no price increases during your contract and no annual inflation-linked price rises. What you sign up at is what you pay." } },
        { "@type": "Question", "name": "Are broadband upgrades free?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Silux Mobile offers free broadband upgrades — move to a faster plan or upgrade your router with no upgrade fee. Free upgrades are included as standard." } }
      ]
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.siluxmobile.co.uk" },
        { "@type": "ListItem", "position": 2, "name": "Broadband", "item": "https://www.siluxmobile.co.uk/broadband" },
        { "@type": "ListItem", "position": 3, "name": "Holiday Home Broadband", "item": "https://www.siluxmobile.co.uk/broadband-holiday-home" }
      ]
    }
  ]
}

const LOCATIONS = [
  "Cornwall", "Lake District", "Scottish Highlands", "Pembrokeshire",
  "Yorkshire Dales", "Norfolk Broads", "New Forest", "Exmoor",
  "Isle of Wight", "Cotswolds", "Peak District", "Northumberland",
]

export default function BroadbandHolidayHome() {
  useEffect(() => {
    setMetaTags({
      title: 'Holiday Home & Holiday Let Broadband UK | No Landline | Silux Mobile',
      description: "Broadband for holiday homes, lodges and holiday lets — no landline, no engineer and nothing to install. 4G & 5G mobile broadband from £15.95/month with the router included, and it moves to another property with you. Plans from 1 month (rolling) to 36 months.",
      keywords: ['holiday home broadband', 'holiday let wifi', 'lodge broadband', 'second home broadband', 'broadband without landline', 'short term broadband', 'mobile broadband UK'],
      canonical: 'https://www.siluxmobile.co.uk/broadband-holiday-home',
    })
    const s = document.createElement('script')
    s.type = 'application/ld+json'
    s.text = JSON.stringify(SCHEMA)
    document.head.appendChild(s)
    return () => { document.head.removeChild(s) }
  }, [])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-600 to-orange-800 text-white py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <nav className="text-amber-300 text-sm mb-6 flex items-center gap-2">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/broadband" className="hover:text-white">Broadband</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Holiday Home Broadband</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Holiday Home Broadband UK</h1>
          <p className="text-xl text-amber-100 mb-3 max-w-2xl">
            Give your holiday cottage or second home reliable WiFi without a landline, a phone socket or an engineer visit. From £15.95 a month, router included.
          </p>
          <p className="text-amber-200 text-base mb-8 max-w-2xl">
            <strong>Holiday home broadband</strong> is one of the best use cases for mobile internet. A rural cottage can wait months and pay thousands for a fixed line — a Silux Mobile router arrives in the post and is working the same day. Choose the term that fits the property, from a 1-month rolling contract to 36 months, and if you ever sell or move, the router and SIM go with you to the next address.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/broadband" className="bg-white text-amber-800 font-semibold px-6 py-3 rounded-lg hover:bg-amber-50 transition">
              See Holiday Home Plans
            </Link>
            <Link to="/network-coverage" className="border border-white text-white px-6 py-3 rounded-lg hover:bg-white/10 transition">
              Check Property Coverage
            </Link>
          </div>
        </div>
      </section>

      <SiluxPromise variant="broadband" />

      {/* Benefits for holiday let owners */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Why Holiday Let Owners Choose Silux Mobile</h2>
          <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
            Fast WiFi is now the #1 feature guests look for on Airbnb and Booking.com. Mobile broadband gives you that — without the fixed-line hassle.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "A Term That Fits the Property", body: "Not sure how long you need it? Go rolling monthly from £25.95/month and give 30 days’ notice when the season ends, or take a 3-month plan from £19.95/month plus a £15 refundable holding deposit. Keeping it for years? £15.95/month over 24 months is our best rate.", icon: "💰" },
              { title: "No Landline Required", body: "Rural holiday cottages often can't get or afford a BT line. Mobile broadband needs nothing — no phone line, no installation.", icon: "📡" },
              { title: "Attract More Bookings", body: "Properties with fast WiFi earn 20–30% more per night on Airbnb. It's the most cost-effective amenity upgrade you can make.", icon: "📈" },
              { title: "Works Across All Devices", body: "A 4G/5G router creates a full home WiFi network. Guests connect phones, laptops, smart TVs, streaming sticks — everything.", icon: "📱" },
              { title: "Instant Setup Before Changeover", body: "Free Tracked 48 delivery in 2–3 working days (faster at checkout). Set up in 5 minutes. No waiting for an engineer — perfect if you need to get a property guest-ready quickly.", icon: "⚡" },
              { title: "Take It With You", body: "If you ever sell or repurpose the property, the SIM and router come with you and work at any UK address with coverage. Nothing to uninstall, nothing to reinstall.", icon: "🧳" },
            ].map(c => (
              <div key={c.title} className="flex gap-4 bg-gray-50 rounded-xl p-5">
                <div className="text-3xl flex-shrink-0">{c.icon}</div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{c.title}</h3>
                  <p className="text-gray-600 text-sm">{c.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-16 px-4 bg-amber-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Holiday Home Broadband Plans</h2>
          <p className="text-gray-500 text-center mb-10">Set it up before your first guests arrive, on the term that suits the property.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Rolling Monthly", price: "£25.95", guests: "As long as you need", features: ["1-month minimum, then month to month", "30 days’ notice to leave", "£30 refundable holding deposit", "Free delivery"] },
              { name: "Season", price: "£19.95", guests: "One season", features: ["3-month plan", "£15 refundable holding deposit", "4G/5G router included", "Free delivery"] },
              { name: "Cottage", price: "£15.95", guests: "Up to 6 guests", features: ["Unlimited data on 4G/5G", "24-month plan — best rate", "4G/5G router included", "Free delivery"], popular: true },
              { name: "Estate", price: "£19.95", guests: "Large properties", features: ["Unlimited 5G Premium data", "36-month plan (12-month £27.95)", "5G router included", "Free delivery"] },
            ].map(p => (
              <div key={p.name} className={`bg-white rounded-2xl p-6 border-2 ${p.popular ? 'border-amber-500 shadow-lg' : 'border-gray-200'} relative`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</div>}
                <p className="text-xs text-gray-500 mb-1">{p.guests}</p>
                <h3 className="text-xl font-bold mb-1">{p.name}</h3>
                <p className="text-3xl font-bold text-amber-700 mb-4">{p.price}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                <ul className="space-y-2 mb-6">
                  {p.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/broadband" className={`block text-center py-2.5 rounded-lg font-semibold text-sm transition ${p.popular ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                  Get This Plan
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular holiday locations */}
      <section className="py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Popular Holiday Home Locations We Cover</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LOCATIONS.map(loc => (
              <div key={loc} className="flex items-center gap-2 bg-amber-50 rounded-lg px-3 py-2">
                <Sun className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="text-sm text-gray-700">{loc}</span>
              </div>
            ))}
          </div>
          <p className="text-center mt-6">
            <Link to="/network-coverage" className="text-amber-700 font-semibold hover:underline">Check coverage at your property postcode →</Link>
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Holiday Home Broadband FAQs</h2>
          <div className="space-y-4">
            {(SCHEMA["@graph"][1] as any).mainEntity.map((q: any) => (
              <details key={q.name} className="bg-white rounded-xl border border-gray-200 p-5 group">
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex justify-between items-center">
                  {q.name}
                  <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-gray-600 text-sm mt-3">{q.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-12 px-4 bg-amber-700 text-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold mb-4 text-center">More from Silux Mobile</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { to: "/broadband-caravan", label: "Caravan Broadband" },
              { to: "/broadband-rural-uk", label: "Rural Broadband" },
              { to: "/mobile-broadband", label: "Mobile Broadband" },
              { to: "/data-sim-cards", label: "Data SIM Cards" },
            ].map(l => (
              <Link key={l.to} to={l.to} className="bg-white/10 hover:bg-white/20 rounded-lg px-4 py-3 text-center text-sm font-medium transition">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
